import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { Add, CalendarMonthOutlined } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { fetchAppointments } from '../../../../services/appointments';
import OverviewCard from './OverviewCard';

const AppointmentSummary = ({
    appointment,
    title,
    emptyLabel,
    notesLabel,
    noNotesLabel
}) => (
    <Box
        sx={{
            minWidth: 0,
            minHeight: 112,
            p: 1.5,
            border: '1px solid #D6DDE2',
            borderRadius: 1.5,
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 3px rgba(38, 50, 56, 0.12)'
        }}
    >
        <Typography
            component="h3"
            sx={{
                color: 'primary.main',
                fontSize: '0.8125rem',
                fontWeight: 700,
                mb: 0.75
            }}
        >
            {title}
        </Typography>

        {appointment ? (
            <>
                <Typography
                    variant="body2"
                    noWrap
                    sx={{ color: '#263238', fontWeight: 700 }}
                >
                    {dayjs(appointment.ts).format('DD/MM/YYYY')}
                    {appointment.doctor?.name ? ` - ${appointment.doctor.name}` : ''}
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: '-webkit-box',
                        mt: 0.75,
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2
                    }}
                >
                    {appointment.note ? notesLabel : noNotesLabel}
                </Typography>
            </>
        ) : (
            <Typography variant="body2" color="text.secondary">
                {emptyLabel}
            </Typography>
        )}
    </Box>
);

AppointmentSummary.propTypes = {
    appointment: PropTypes.shape({
        ts: PropTypes.string,
        note: PropTypes.string,
        doctor: PropTypes.shape({ name: PropTypes.string })
    }),
    title: PropTypes.string.isRequired,
    emptyLabel: PropTypes.string.isRequired,
    notesLabel: PropTypes.string.isRequired,
    noNotesLabel: PropTypes.string.isRequired
};

const AppointmentsOverviewCard = ({ patientUuid, onSchedule, onViewAppointments }) => {
    const { t } = useTranslation();
    const today = dayjs().format('YYYY-MM-DD');
    const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');

    const upcomingQuery = useQuery({
        queryKey: ['patient-appointments', patientUuid, 'upcoming', today],
        queryFn: () =>
            fetchAppointments({
                patient: patientUuid,
                timestamp: new Date().toISOString(),
                statuses: ['SCHEDULED'],
                order: 'ASC',
                pageLimit: 1
            }),
        enabled: Boolean(patientUuid),
        staleTime: 60 * 1000
    });

    const completedQuery = useQuery({
        queryKey: ['patient-appointments', patientUuid, 'latest-completed', tomorrow],
        queryFn: () =>
            fetchAppointments({
                patient: patientUuid,
                statuses: ['COMPLETED'],
                order: 'DESC',
                pageLimit: 1
            }),
        enabled: Boolean(patientUuid),
        staleTime: 60 * 1000,
        refetchOnMount: 'always'
    });

    const latestCompleted = useMemo(
        () =>
            (Array.isArray(completedQuery.data) ? completedQuery.data : [])
                .filter((appointment) => appointment.status === 'COMPLETED')
                .sort((a, b) => dayjs(b.ts).valueOf() - dayjs(a.ts).valueOf())[0],
        [completedQuery.data]
    );

    const nextAppointment = useMemo(
        () =>
            (Array.isArray(upcomingQuery.data) ? upcomingQuery.data : [])
                .filter(
                    (appointment) =>
                        appointment.status === 'SCHEDULED' &&
                        dayjs(appointment.ts).isAfter(dayjs())
                )
                .sort((a, b) => dayjs(a.ts).valueOf() - dayjs(b.ts).valueOf())[0],
        [upcomingQuery.data]
    );

    const isPending = upcomingQuery.isPending || completedQuery.isPending;
    const isError = upcomingQuery.isError || completedQuery.isError;

    return (
        <OverviewCard
            title={t('userPage.overview.consultations')}
            icon={<CalendarMonthOutlined fontSize="small" />}
            contentInset={4}
            action={
                <Tooltip title={t('userPage.create_consultation')}>
                    <IconButton
                        size="small"
                        color="primary"
                        aria-label={t('userPage.create_consultation')}
                        onClick={onSchedule}
                    >
                        <Add />
                    </IconButton>
                </Tooltip>
            }
            minHeight={230}
        >
            {isPending ? (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 1.5
                    }}
                >
                    <Skeleton variant="rounded" height={112} />
                    <Skeleton variant="rounded" height={112} />
                </Box>
            ) : isError ? (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5
                    }}
                >
                    <Typography color="error">
                        {t('userPage.overview.appointments.loadError')}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                            upcomingQuery.refetch();
                            completedQuery.refetch();
                        }}
                    >
                        {t('userPage.overview.measurements.retry')}
                    </Button>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 1.5
                    }}
                >
                    <AppointmentSummary
                        title={t('userPage.overview.appointments.latest')}
                        appointment={latestCompleted}
                        emptyLabel={t('userPage.overview.appointments.noCompleted')}
                        notesLabel={t('userPage.overview.appointments.notes')}
                        noNotesLabel={t('userPage.overview.appointments.noNotes')}
                    />
                    <AppointmentSummary
                        title={t('userPage.overview.appointments.next')}
                        appointment={nextAppointment}
                        emptyLabel={t('userPage.overview.appointments.noUpcoming')}
                        notesLabel={t('userPage.overview.appointments.notes')}
                        noNotesLabel={t('userPage.overview.appointments.noNotes')}
                    />
                </Box>
            )}

            <Button
                size="small"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={onViewAppointments}
                sx={{
                    alignSelf: 'flex-end',
                    mt: 'auto',
                    mr: -4,
                    px: 0.5,
                    fontWeight: 600
                }}
            >
                {t('userPage.overview.viewConsultations')}
            </Button>
        </OverviewCard>
    );
};

AppointmentsOverviewCard.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    onSchedule: PropTypes.func.isRequired,
    onViewAppointments: PropTypes.func.isRequired
};

export default AppointmentsOverviewCard;
